/* eslint-disable no-undef */
import React, { useMemo } from "react";
import { mergeStyleSets, Modal } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { DocumentPartTree, TreeViewDataItem } from "./DocumentPartTree";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { openDocumentById } from "../../utils/helpers/OpenDocumentHelper";
import FooterPanel from "../layout/FooterPanel";
import HeaderPanel from "../layout/HeaderPanel";
import { DocumentPart } from "../../api/models";

export type DocumentPropertiesActionTypes = "confirmNumberOfFathers" | "confirmChildrenDeletion" | "confirm";

export interface DocumentPropertiesMessagesProps {
  isModalOpen: boolean;
  onHideModal: any;
  onDeleteConfirm?: (documentPart: DocumentPart) => Promise<void>;
  selectedItemFathers?: TreeViewDataItem[];
  deleteDocumentPart?: () => Promise<void>;
  deleteItem: DocumentPart;
  action: DocumentPropertiesActionTypes;
}

const contentStyles = mergeStyleSets({
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
    minHeight: "100vh",
    minWidth: "100vw",
  },
});

export const DocumentPropertiesMessagesModal: React.FunctionComponent<DocumentPropertiesMessagesProps> = (props) => {
  const { isModalOpen, onHideModal, onDeleteConfirm, deleteDocumentPart, deleteItem, action, selectedItemFathers } =
    props;

  function modalHide() {
    onHideModal();
  }

  async function sendResponse() {
    try {
      if (action === "confirmNumberOfFathers") await onDeleteConfirm(deleteItem);
      if (action === "confirmChildrenDeletion") await deleteDocumentPart();
      if (action === "confirm") await deleteDocumentPart();
    } catch {
      modalHide();
    }
  }

  const { details, message, accentColor } = useMemo(() => {
    if (action === "confirmNumberOfFathers")
      return {
        message: formatMessage("cof.documentproperties.delete.message.request"),
        details: formatMessageWithParams("cof.documentproperties.delete.message", { linkedPart: deleteItem?.name }),
        accentColor: "var(--color-red)",
      };
    if (action === "confirmChildrenDeletion")
      return {
        message: formatMessageWithParams("cof.documentproperties.confirmchildrendeletion.message", {
          linkedPart: deleteItem?.name,
        }),
        accentColor: "var(--color-yellow)",
      };
    if (action === "confirm")
      return {
        message: formatMessage("cof.documentproperties.delete.message.request"),
        details: formatMessage("cof.documentproperties.confirmdelete.closedocuments"),
        accentColor: "var(--color-yellow)",
      };
    return {};
  }, [deleteItem]);

  return (
    <Modal titleAriaId={"titleId"} isOpen={isModalOpen} containerClassName={contentStyles.container}>
      <HeaderPanel applyPadding borderColor={accentColor}>
        {formatMessageWithParams("cof.file.open.outline.title.documentoutline", {
          documentName: deleteItem?.name.replace(/\.docx$/, ""),
        })}
      </HeaderPanel>
      <div
        className="messageText"
        style={{
          margin: "12px 8px",
          padding: "8px 8px",
          borderRadius: "8px",
          backgroundColor: accentColor,
          color: "white",
        }}
      >
        {message}
      </div>
      {details && (
        <div className="bodyText" style={{ margin: "0 8px 12px 8px" }}>
          {details}
        </div>
      )}
      {action === "confirmNumberOfFathers" && selectedItemFathers && (
        <DocumentPartTree
          selectedPartId={undefined}
          rootParts={selectedItemFathers}
          expandIcons={false}
          openDocument={openDocumentById}
        />
      )}
      <FooterPanel>
        <PrimaryButton
          id="confirm_button"
          data-testid="scratch"
          onClick={() => sendResponse()}
          text={formatMessage("cof.base.yes")}
          style={{ float: "right" }}
        />
        <PrimaryButton
          id="cancel_button"
          text={formatMessage("cof.base.no")}
          style={{ float: "left" }}
          onClick={() => modalHide()}
        />
      </FooterPanel>
    </Modal>
  );
};
